/* eslint-disable complexity */
import { inject } from 'mobx-react';
import { ExperimentStore } from 'bernie-plugin-mobx';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { PackageType } from 'models/multi-item/multi-item-types';

import { UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { FormFieldBinding } from './form-field-binding';
import { getGridItemProps } from '../grid-item-props';
import { NewDateField } from './new-date-field';
import { SearchFormFieldProps } from 'src/common/components/common/search-form';

export interface DateRangeState {
  endDateFocus?: () => void;
  startDateFocus?: () => void;
}

export type LocalizedDateRateProps = LocalizedComponentProps & SearchFormFieldProps;

export interface DateRangeProps extends LocalizedDateRateProps {
  currentHotel?: CurrentHotelStore;
  fetchDatePickerRates?: () => Promise<object>;
  multiItem?: MultiItemStore;
  experiment?: ExperimentStore;
}

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');

@withLocalization
@FormFieldBinding
@inject('currentHotel', 'multiItem', 'experiment')
export class DateRange extends React.Component<DateRangeProps, DateRangeState> {
  constructor(props: any) {
    super(props);

    this.state = {
      endDateFocus: undefined,
      startDateFocus: undefined,
    };

    this.handleChange = this.handleChange.bind(this);
    this.registerFocusMethod = this.registerFocusMethod.bind(this);
    this.validate = this.validate.bind(this);

    const { registerValidator } = props;

    registerValidator(() => this.validate('startDate'));
    registerValidator(() => this.validate('endDate'));
  }

  private handleChange(event: any) {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel(event);
    }
  }

  private logDateRangeError(startDateError: any, endDateError: any, startDate: any, endDate: any) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_DATE_RANGE_ERROR'),
      `${startDateError} | ${endDateError}`,
      `startDate=${startDate} endDate=${endDate}`
    );
  }

  private updateModels(startDate: string, endDate: string) {
    this.handleChange({
      target: {
        name: 'startDate',
        value: startDate,
      },
    });

    this.handleChange({
      target: {
        name: 'endDate',
        value: endDate,
      },
    });
  }

  public render() {
    const { l10n, model, fetchDatePickerRates, multiItem } = this.props;
    const { formatText } = l10n;
    const startDate = model.startDate && this.isValidDate(new Date(model.startDate)) ? model.startDate : undefined;
    const endDate = model.endDate && this.isValidDate(new Date(model.endDate)) ? model.endDate : undefined;
    const buttonText = formatText('hotels.roomPicker.doneButton.label');

    const startDateError = this.validate('startDate');
    const endDateError = this.validate('endDate');

    const commonDateFieldProps = {
      startDate,
      endDate,
      placeholder: formatText('hotels.search.form.toDate.placeholder'),
      numDaysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffsetHours: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      buttonText,
      registerFocusMethod: this.registerFocusMethod,
      updateModels: this.updateModels.bind(this),
    };

    if (logger && typeof logger.logEvent === 'function' && (startDateError || endDateError)) {
      this.logDateRangeError(startDateError, endDateError, startDate, endDate);
    }

    const routerLinkWrapper = (children: any) => <>{children}</>;

    const isFlightInSearch = multiItem && multiItem.getPackageType() && multiItem.getPackageType() !== PackageType.HC;
    const { startDateLabel, endDateLabel } = isFlightInSearch
      ? {
          startDateLabel: formatText('hotels.searchWizard.multiitem.departing.date'),
          endDateLabel: formatText('hotels.searchWizard.multiitem.returning.date'),
        }
      : {
          startDateLabel: formatText('hotels.search.form.fromDateHyphen.label'),
          endDateLabel: formatText('hotels.search.form.toDateHyphen.label'),
        };

    const dateFieldProps = {
      endId: 'hotels-check-out',
      endInvalid: endDateError && endDateError.text,
      endLabel: endDateLabel,
      endName: 'endDate',
      endPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
      routerLinkWrapper,
      startId: 'hotels-check-in',
      startInvalid: startDateError && startDateError.text,
      startLabel: startDateLabel,
      startName: 'startDate',
      startPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
    };

    const rfrr = {
      end: ':ChangeDatesCheckOut',
      start: ':ChangeDatesCheckIn',
    };

    return (
      <UitkSpacing
        margin={{
          small: {
            block: 'half',
          },
          medium: {
            block: 'unset',
          },
        }}
      >
        <UitkLayoutGridItem {...getGridItemProps('dateRange', multiItem)}>
          <div>
            <NewDateField
              rfrr={rfrr}
              dateFieldProps={dateFieldProps}
              fetchDatePickerRates={fetchDatePickerRates}
              {...(commonDateFieldProps as any)}
            />
          </div>
        </UitkLayoutGridItem>
      </UitkSpacing>
    );
  }

  private registerFocusMethod(fieldName: string, focusMethod: () => void) {
    this.setState({ [`${fieldName}Focus`]: focusMethod });
  }

  private validate(key: string) {
    const { l10n, model } = this.props;
    const { startDateFocus, endDateFocus } = this.state;

    const { formatText } = l10n;

    const { endDate, startDate } = model;

    if (key === 'startDate') {
      const start = startDate && new Date(startDate);
      const message = 'hotels.search.validation.invalidFromDate.text';

      if ((!start || !this.isValidDate(start)) && !model.isDateless) {
        return {
          text: formatText(message),
          focus: startDateFocus,
        };
      }
    }

    if (key === 'endDate') {
      const start: any = startDate ? new Date(startDate) : undefined;
      const end: any = endDate ? new Date(endDate) : undefined;
      const message = 'hotels.search.validation.invalidToDate.text';

      if ((!end || !this.isValidDate(end)) && !model.isDateless) {
        return {
          text: formatText(message),
          focus: endDateFocus,
        };
      }

      if (start && this.isValidDate(start)) {
        const maxEndDate = new Date(start.getFullYear(), start.getMonth(), (start.getDate() as number) + 28);

        if (start && end && end < start) {
          return {
            text: formatText('hotels.search.validation.invalidDateRange.text'),
            focus: endDateFocus,
          };
        }

        if (maxEndDate) {
          maxEndDate.setHours(0, 0, 0, 0);
        }

        end?.setHours(0, 0, 0, 0);

        if (maxEndDate !== null && end && end > maxEndDate) {
          return {
            text: formatText('hotels.search.validation.invalidDateDateRangeLength.text'),
            focus: endDateFocus,
          };
        }
      }
    }
  }

  private isValidDate(date: Date | string) {
    return date.toString() !== 'Invalid Date';
  }
}
